var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as FullStory from '@fullstory/browser';
import * as Sentry from '@sentry/react';
import LogRocket from 'logrocket';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useLocalStorage } from 'react-use';
import UserInfoApi from '~/api/user_info_engine/userInfoService';
import { useAuth } from '~/providers/auth';
import Permissions from '~/settings/permissions';
import { keysToCamel } from '~/utils/toCamelCase';
import { useTaggableClauseUsers, useTaggableComparisonUsers } from './hooks';
const getOrgCookieName = () => {
    let orgCookieName = 'rgi-org-id';
    if (window.RG_CONFIG.ENVIRONMENT !== 'prod') {
        if (window.RG_CONFIG.ENVIRONMENT === 'local') {
            orgCookieName += '-dev';
        }
        else {
            orgCookieName += '-' + window.RG_CONFIG.ENVIRONMENT;
        }
    }
    return orgCookieName;
};
const UserContext = createContext({});
const UserProvider = (props) => {
    const { isAuthenticated } = useAuth();
    const orgCookieName = getOrgCookieName();
    const [cookies, setCookie] = useCookies();
    const [id, setId] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [permissions, setPermissions] = useState([]);
    const [privilegeGroups, setPrivilegeGroups] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const taggableClauseUsers = useTaggableClauseUsers(permissions);
    const taggableComparisonUsers = useTaggableComparisonUsers(permissions);
    const [activeOrganization, setActiveOrganization] = useState();
    const [isFetchingUserInfo, setIsFetchingUserInfo] = useState(false);
    const [hasUserInfo, setHasUserInfo] = useState(false);
    const [viewOverride, setViewOverride] = useLocalStorage('clauselink-new-view-override');
    useEffect(() => {
        if (isAuthenticated && !hasUserInfo) {
            fetchUserInfo();
        }
        else if (!isAuthenticated && hasUserInfo) {
            setId('');
            setEmail('');
            setName('');
            setPermissions([]);
            setPrivilegeGroups([]);
            setOrganizations([]);
        }
    }, [isAuthenticated, hasUserInfo]);
    useEffect(() => {
        var _a;
        if (hasUserInfo) {
            (_a = document.getElementById('preload')) === null || _a === void 0 ? void 0 : _a.remove();
        }
    }, [hasUserInfo]);
    const switchOrganization = (newActiveOrg) => {
        setCookie(orgCookieName, newActiveOrg.id, {
            path: '/',
            domain: '.boldpenguin.com',
            secure: true,
            sameSite: 'none',
        });
        setActiveOrganization(newActiveOrg);
        window.location.reload();
    };
    const fetchUserInfo = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsFetchingUserInfo(true);
        const userConfig = keysToCamel(yield UserInfoApi.getUserInfo());
        setId(userConfig.id);
        setEmail(userConfig.email);
        setName(userConfig.name);
        setPermissions(userConfig.permissions);
        setPrivilegeGroups(userConfig.privilegeGroups);
        setOrganizations(userConfig.organizations);
        const cookieOrgId = cookies[getOrgCookieName()];
        let activeOrg = userConfig.organizations[0];
        if (cookieOrgId) {
            const cookieOrg = userConfig.organizations.find(org => org.id === cookieOrgId);
            if (cookieOrg)
                activeOrg = cookieOrg;
        }
        setCookie(getOrgCookieName(), activeOrg.id, {
            domain: '.boldpenguin.com',
            path: '/',
            sameSite: 'none',
            secure: true,
        });
        setActiveOrganization(activeOrg);
        Sentry.setContext('Organization', activeOrg);
        let title = 'ClauseLink';
        if (window.RG_CONFIG.ENVIRONMENT !== 'prod') {
            const env = window.RG_CONFIG.ENVIRONMENT;
            title += ' - ' + env.charAt(0).toLocaleUpperCase() + env.slice(1).toLocaleLowerCase();
        }
        FullStory.setUserVars({
            displayName: userConfig.name,
            email: userConfig.email,
            permissions,
            organizations,
            site_title: title,
        });
        LogRocket.identify(userConfig.email, {
            name: userConfig.name,
            email: userConfig.email,
        });
        setIsFetchingUserInfo(false);
        setHasUserInfo(true);
    });
    const hasPermission = useCallback((hasPerm) => {
        return !!permissions.find(permission => permission === hasPerm);
    }, [permissions]);
    const hasNewViewPermission = permissions.includes(Permissions.ACCESS_CLAUSELINK);
    const isBoldPenguinEmail = email.endsWith('@boldpenguin.com');
    const canToggleView = hasNewViewPermission && isBoldPenguinEmail;
    const useNewView = hasNewViewPermission && (canToggleView ? (viewOverride !== null && viewOverride !== void 0 ? viewOverride : true) : true);
    useEffect(() => {
        Sentry.setContext('Use New View', { useNewView });
    }, [useNewView]);
    const setUseNewView = (value) => {
        if (!canToggleView)
            return;
        setViewOverride(value);
        window.location.href = '/';
    };
    const value = {
        id,
        name,
        email,
        organizations,
        activeOrganization: activeOrganization,
        switchOrganization,
        fetchUserInfo,
        taggableClauseUsers,
        taggableComparisonUsers,
        isFetchingUserInfo,
        hasPermission,
        permissions,
        privilegeGroups,
        hasUserInfo,
        useNewView,
        canToggleView,
        setUseNewView,
    };
    return React.createElement(UserContext.Provider, Object.assign({ value: value }, props));
};
const useUser = () => {
    const ctx = useContext(UserContext);
    if (ctx === undefined) {
        throw new Error('UserContext access only works inside of a UserContext.Provider');
    }
    return ctx;
};
export { UserProvider, useUser };
